// Import Core
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
// Import Icons
import { LiaTimesSolid } from 'react-icons/lia';

/* Styled Components */
const PopoverStyling = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;

	&[data-popover-open="true"] {
		z-index: 1100;
		position: relative;
	}

	.popover-wrapper {
		align-items: center;
		display: flex;
		justify-content: center;
		position: relative;
		z-index: 1;

		.popover {
			align-items: center;
			background-color: var(--red);
			box-shadow: var(--box-shadow-small);
			display: flex;
			padding: 12px 24px;
			position: absolute;
			white-space: normal;
			z-index: 1050;
			--popover-x-spacing: 15px;
			--popover-y-spacing: 70px;

			&::before {
				display: none;
			}
			.popover-content {
				color: white;
				font-size: 16px;
				position: relative;
				width: 100%;

				display: flex;
				max-width: 250px;
				width: max-content;
				flex-direction: column;
				align-items: flex-start;
				gap: 4px;
				flex-flow: wrap;

				.popover-close {
					position: absolute;
					cursor: pointer;
					right: 0;
					height: 32px;
					width: 32px;
					display: flex;
					justify-content: center;
					align-items: center;
					svg {
						width: 15px;
						height: auto;
					}
				}
			}
			&.popover-top {
				top: calc(var(--popover-y-spacing, 50px) * -1);
			}

			&.popover-center {
				top: 50%;
				left: 50%;
				transform: translate(-50%, -55%);
				@media (max-width: 768px) {
					top: 0;
					transform: translate(-50%, -100%);
				}
			}

			&.popover-right {
				left: calc(100% + var(--popover-x-spacing, 40px));
				top: auto;
			}

			&.popover-bottom {
				bottom: calc(var(--popover-y-spacing, 50px) * -1);
			}

			&.popover-left {
				right: calc(100% + var(--popover-x-spacing, 40px));
				top: auto;
			}
		}
	}
`;
/* Render Component */
const Popover = ({
	isPopoverActive,
	activePopoverId,
	togglePopover,
	content,
	showClose = 'false',
	placement = 'top',
	id,
	trigger,
	onClose,
}) => {

	// Get cookie value
	const getCookie = (name) => {
		if (typeof document === 'undefined') return null;
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) return parts.pop().split(';').shift();
		return null;
	};

	const popoverWrapperRef = useRef(null);
	const listenerAddedRef = useRef(false);

	// Close popover when clicking outside (defer listener so opening click doesn't close immediately)
	useEffect(() => {
		if (!isPopoverActive || activePopoverId !== id) return;
		const handleClickOutside = (e) => {
			if (popoverWrapperRef.current && !popoverWrapperRef.current.contains(e.target)) {
				togglePopover(id);
			}
		};
		listenerAddedRef.current = false;
		const frame = requestAnimationFrame(() => {
			document.addEventListener('click', handleClickOutside, true);
			listenerAddedRef.current = true;
		});
		return () => {
			cancelAnimationFrame(frame);
			if (listenerAddedRef.current) {
				document.removeEventListener('click', handleClickOutside, true);
				listenerAddedRef.current = false;
			}
		};
	}, [isPopoverActive, activePopoverId, id, togglePopover]);

	// Show newsletter popover after 15 seconds (if cookie is not set)
	useEffect(() => {
		if (id === 'newsletter-popover') {
			const cookieValue = getCookie('newsletter-popover');
			const submittedValue = getCookie('newsletter-submitted');
			if (!cookieValue && !submittedValue) {
				const timer = setTimeout(() => {
					togglePopover('newsletter-popover');
				}, 15000); // 15 seconds

				// Cleanup timer on unmount
				return () => clearTimeout(timer);
			}
		}
	}, []); // Runs once per mount

	const handleTriggerClick = (e) => {
		e.stopPropagation();
		togglePopover(id);
	};

	const isOpen = isPopoverActive && activePopoverId === id;

	return (
		<PopoverStyling
			id={id}
			className="popover"
			data-popover-open={isOpen ? 'true' : undefined}
		>
			<div className='popover-wrapper' ref={popoverWrapperRef}>
				<div className='popover-children' onClick={handleTriggerClick}>
					{trigger}
				</div>

				{isPopoverActive && activePopoverId === id && (
					<div className={`popover popover-${placement}`}>
						<div className='popover-content'>
							{showClose && (
								<div className='popover-close' onClick={(e) => {
									e.stopPropagation();
									togglePopover(id);
									if (onClose) {
										onClose();
									}
								}}>
									<LiaTimesSolid />
								</div>
							)}
							{content}
						</div>
					</div>
				)}
			</div>
		</PopoverStyling>
	);
};

export default Popover;

